import React from 'react'
import type { AssignmentSource } from '../../api/jobAssignmentDefaults'

type CrewOption = { id: string; name: string }
type EmployeeOption = { id: string; first_name: string; last_name: string }

type VisitGroupAssignee = {
  id: string
  name?: string | null
  dsny_integration?: boolean | null
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
}

interface ContractAssignmentSummaryProps {
  assignmentSource?: AssignmentSource | string | null
  defaultCrewId?: string | null
  defaultEmployeeId?: string | null
  propertyDefaultCrewName?: string | null
  propertyDefaultEmployeeName?: string | null
  visitGroups?: VisitGroupAssignee[]
  crews: CrewOption[]
  employees: EmployeeOption[]
  showDsnyVisitTargets?: boolean
}

function labelCrew(crews: CrewOption[], id?: string | null): string | null {
  if (!id) return null
  return crews.find((c) => c.id === id)?.name || null
}

function labelEmployee(employees: EmployeeOption[], id?: string | null): string | null {
  if (!id) return null
  const e = employees.find((emp) => emp.id === id)
  return e ? `${e.first_name} ${e.last_name}`.trim() : null
}

function formatPair(
  crewName: string | null,
  employeeName: string | null,
  emptyLabel = 'Inherit / unassigned'
): string {
  const parts = [crewName, employeeName].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : emptyLabel
}

function labelEmployeeOrId(
  employees: EmployeeOption[],
  id?: string | null
): string | null {
  if (!id) return null
  return labelEmployee(employees, id) || 'Assigned employee'
}

function sourceLabel(source?: AssignmentSource | string | null): string {
  if (source === 'custom') return 'Custom for this contract'
  if (source === 'unassigned') return 'Dispatcher assigns (leave unassigned)'
  return 'Use property default'
}

/**
 * Read-only Job Assignment + Exterior/Interior visit assignees for Contract View.
 */
export function ContractAssignmentSummary({
  assignmentSource,
  defaultCrewId,
  defaultEmployeeId,
  propertyDefaultCrewName,
  propertyDefaultEmployeeName,
  visitGroups = [],
  crews,
  employees,
  showDsnyVisitTargets = false,
}: ContractAssignmentSummaryProps) {
  const source = (assignmentSource || 'property_default') as AssignmentSource

  let jobCrew: string | null = null
  let jobEmployee: string | null = null
  if (source === 'custom') {
    jobCrew = labelCrew(crews, defaultCrewId)
    jobEmployee = labelEmployee(employees, defaultEmployeeId)
  } else if (source === 'property_default') {
    jobCrew = propertyDefaultCrewName || null
    jobEmployee = propertyDefaultEmployeeName || null
  }

  const exteriorGroups = visitGroups.filter((g) => g.dsny_integration)
  const interiorGroups = visitGroups.filter((g) => /^Hallway Cleaning\b/i.test(g.name || ''))
  const exteriorSample = exteriorGroups.find((g) => g.assigned_crew_id || g.assigned_employee_id) ||
    exteriorGroups[0]
  const interiorSample = interiorGroups.find((g) => g.assigned_crew_id || g.assigned_employee_id) ||
    interiorGroups[0]

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Job Assignment</h2>
        <p className="text-sm text-gray-600 mt-1">Default crew / employee for jobs on this contract.</p>
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="font-medium text-gray-600">Assignment source</dt>
          <dd className="mt-1 text-gray-900">{sourceLabel(source)}</dd>
        </div>
        <div>
          <dt className="font-medium text-gray-600">Crew / team</dt>
          <dd className="mt-1 text-gray-900">
            {source === 'unassigned' ? 'Unassigned' : jobCrew || 'None'}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-600">Employee / rep</dt>
          <dd className="mt-1 text-gray-900">
            {source === 'unassigned' ? 'Unassigned' : jobEmployee || 'None'}
          </dd>
        </div>
      </dl>

      {showDsnyVisitTargets && (exteriorGroups.length > 0 || interiorGroups.length > 0) && (
        <div className="pt-3 border-t border-gray-100 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Visit assignees</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exteriorGroups.length > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold text-gray-900">Exterior / DSNY visits</p>
                <p className="text-sm text-gray-800 mt-1">
                  {formatPair(
                    labelCrew(crews, exteriorSample?.assigned_crew_id),
                    labelEmployeeOrId(employees, exteriorSample?.assigned_employee_id)
                  )}
                </p>
              </div>
            )}
            {interiorGroups.length > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold text-gray-900">Interior / Hallway visits</p>
                <p className="text-sm text-gray-800 mt-1">
                  {formatPair(
                    labelCrew(crews, interiorSample?.assigned_crew_id),
                    labelEmployeeOrId(employees, interiorSample?.assigned_employee_id)
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Compact label for a single visit group row. */
export function formatVisitGroupAssigneeLabel(
  group: VisitGroupAssignee,
  crews: CrewOption[],
  employees: EmployeeOption[]
): string | null {
  const crew = labelCrew(crews, group.assigned_crew_id)
  const emp = labelEmployee(employees, group.assigned_employee_id)
  if (!crew && !emp) return null
  return formatPair(crew, emp, '')
}
